"use client"

import { ChevronRight, type LucideIcon } from "lucide-react"
import { useCallback, useEffect, useState } from "react"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarMenuBadge,
} from "@/components/ui/sidebar"
import { NavigationProps, AppPage } from "@/types/navigation"
import { Badge } from "@/components/ui/badge"
import { useNotifications } from "@/contexts/NotificationsContext"
import { useSidebar } from "@/components/ui/sidebar"
import { cn } from "@/lib/utils"
import { URL_TO_PAGE_MAP } from "@/routes/routes"

export function NavMain({
  items,
  onNavigate,
}: {
  items: {
    title: string
    url: string
    icon?: LucideIcon
    isActive?: boolean
    items?: {
      title: string
      url: string
    }[]
  }[]
} & NavigationProps) {
  const { unreadCount, markAsRead } = useNotifications()
  const { state } = useSidebar()
  
  // Function to mark a notification as read
  const handleMarkAsRead = useCallback(async (id: string) => {
    await markAsRead(id)
  }, [markAsRead])
  
  // Handle navigation for sidebar items
  const handleNavigation = useCallback((url: string) => {
    const page = URL_TO_PAGE_MAP[url];
    if (page) {
      onNavigate(page);
      return true;
    }
    
    return false;
  }, [onNavigate]);
  
  // Special handler for notifications badge click
  const handleNotificationBadgeClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent parent click event
    handleNavigation("/notifications");
  }, [handleNavigation]);
  
  // Custom notification badge component that works in both expanded and collapsed states
  const NotificationBadge = useCallback(({ className }: { className?: string }) => {
    if (unreadCount <= 0) return null;
    
    return (
      <>
        {/* Badge for expanded state */}
        <SidebarMenuBadge className="group-data-[collapsible=icon]:hidden">
          <Badge 
            className={cn(
              "ml-2 h-5 min-w-5 flex items-center justify-center bg-primary text-primary-foreground cursor-pointer",
              className
            )} 
            onClick={handleNotificationBadgeClick}
          >
            {unreadCount}
          </Badge>
        </SidebarMenuBadge>
        
        {/* Badge for collapsed state */}
        <div 
          className={cn(
            "absolute top-0.5 right-0.5 hidden group-data-[collapsible=icon]:flex z-10",
            className
          )}
        >
          <Badge 
            className="h-4 min-w-4 flex items-center justify-center p-0 text-xs bg-primary text-primary-foreground cursor-pointer ring-1 ring-background" 
            onClick={handleNotificationBadgeClick}
          >
            {unreadCount}
          </Badge>
        </div>
      </>
    );
  }, [unreadCount, handleNotificationBadgeClick]);
  
  return (
    <SidebarGroup>
      <SidebarGroupLabel>Platform</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => {
          // Check if the item has child items
          const hasChildren = !!item.items?.length;
          
          return hasChildren ? (
            <Collapsible
              key={item.title}
              asChild
              defaultOpen={item.isActive}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton 
                    tooltip={item.title}
                    onClick={() => handleNavigation(item.url)}
                    className="relative"
                    isActive={item.isActive}
                  >
                    {item.icon && <item.icon />}
                    <span>{item.title}</span>
                    {item.title === "Notifications" && <NotificationBadge />}
                    <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {item.items?.map((subItem) => (
                      <SidebarMenuSubItem key={subItem.title}>
                        <SidebarMenuSubButton 
                          onClick={(e) => {
                            if (handleNavigation(subItem.url)) {
                              e.preventDefault();
                            }
                          }}
                          asChild
                        >
                          <a href={subItem.url}>
                            <span>{subItem.title}</span>
                          </a>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          ) : (
            // Render a simple menu item for items without children
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton 
                tooltip={item.title}
                onClick={() => handleNavigation(item.url)}
                className="relative"
                isActive={item.isActive}
              >
                {item.icon && <item.icon />}
                <span>{item.title}</span>
                {item.title === "Notifications" && <NotificationBadge />}
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}
